/**
 * General site scripts (previously web/lib/js/scripts.js).
 * GSAP/ScrollTrigger bundled via npm. All jQuery removed — vanilla JS only.
 */

import GLightbox from 'glightbox';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import 'glightbox/dist/css/glightbox.min.css';
import { initClipPathReveal } from '../animations/clipPathReveal';

gsap.registerPlugin(ScrollTrigger);

// ScrollTrigger.refresh() scrolls to 0 to measure trigger positions and then
// restores. With `html { scroll-behavior: smooth }` Safari animates the trip
// to 0 and ignores the instant restore, ending up at scrollY=0. Disable
// smooth scrolling for the duration of every refresh.
ScrollTrigger.addEventListener('refreshInit', () => {
    document.documentElement.style.scrollBehavior = 'auto';
});
ScrollTrigger.addEventListener('refresh', () => {
    document.documentElement.style.scrollBehavior = '';
});

export function initSiteScripts() {
    initFadeAnimations();
    initWallOfFame();
    initGalleryReveal();
    initDatepicker();
    initNavMenu();
    initCalendarDropdown();
    initLightbox();
}

/** GSAP fade-in-from-bottom on scroll */
function initFadeAnimations() {
    const elements = gsap.utils.toArray<HTMLElement>('.fade-in-bottom');
    elements.forEach((el) => {
        const animation = gsap.fromTo(
            el,
            { autoAlpha: 0, y: 50 },
            { duration: 1, autoAlpha: 1, y: 0 }
        );
        ScrollTrigger.create({
            trigger: el,
            animation,
            start: 'top 95%',
        });
    });
}

/** Wall of Fame clip-path reveal (previously inline in wall-of-fame.twig) */
function initWallOfFame() {
    initClipPathReveal('.wallOfFame .bigbox', 'div', 3);
}

/** Afbeeldinggallerij clip-path reveal (previously inline in afbeeldinggallerij.twig) */
function initGalleryReveal() {
    initClipPathReveal('.boxBoxGallery', 'a', 13);
}

/** Move datepicker into form field */
function initDatepicker() {
    const datepicker = document.querySelector('div.datepicker');
    const target = document.querySelector('form .datefield');
    if (datepicker && target) {
        target.appendChild(datepicker);
    }
}

/** Mobile navigation menu toggle (includes burger icon animation) */
function initNavMenu() {
    const navBtn = document.querySelector('.navBtn');
    const navMenu = document.querySelector('.navigationMenu');
    const navMenuInner = document.querySelector('.navigationMenu > div') as HTMLElement | null;
    const maillink = document.querySelector('.maillink');
    const menubox = document.querySelector('.menubox');
    const menuoverlay = document.querySelector('.menuoverlay');
    const nav = document.querySelector('nav');
    const burgerLines = document.querySelectorAll('.burger-line');

    if (!navBtn) return;

    let isOpen = false;

    function toggleBurgerIcon(open: boolean) {
        if (burgerLines.length < 3) return;
        const [top, mid, bot] = Array.from(burgerLines);
        if (open) {
            top.classList.add('rotate-45');
            top.classList.remove('-translate-y-1.5');
            mid.classList.add('opacity-0');
            bot.classList.add('-rotate-45');
            bot.classList.remove('translate-y-1.5');
        } else {
            top.classList.remove('rotate-45');
            top.classList.add('-translate-y-1.5');
            mid.classList.remove('opacity-0');
            bot.classList.remove('-rotate-45');
            bot.classList.add('translate-y-1.5');
        }
    }

    navBtn.addEventListener('click', () => {
        isOpen = !isOpen;
        toggleBurgerIcon(isOpen);

        if (!isOpen) {
            navMenu?.classList.remove('menuOpen');
            navMenu?.classList.add('menuClosed', '-mr-100');
            if (navMenuInner) navMenuInner.style.marginRight = '-100%';
            maillink?.classList.remove('menuOpen');
            menubox?.classList.remove('menuOpen');
            menuoverlay?.classList.remove('active');
            nav?.classList.remove('navopen');
        } else {
            navMenu?.classList.add('menuOpen');
            navMenu?.classList.remove('menuClosed');
            if (navMenuInner) navMenuInner.removeAttribute('style');
            menubox?.classList.add('menuOpen');
            maillink?.classList.add('menuOpen');
            menuoverlay?.classList.add('active');
            nav?.classList.add('navopen');
        }
    });

    // Click outside nav menu to close
    navMenu?.addEventListener('click', (event) => {
        if ((event.target as HTMLElement).nodeName !== 'A') {
            (navBtn as HTMLElement).click();
        }
    });
}

/** Calendar month dropdown toggle */
function initCalendarDropdown() {
    const calendarBtn = document.getElementById('calendar-month-menu');
    if (!calendarBtn) return;

    calendarBtn.addEventListener('click', () => {
        const menu = calendarBtn.nextElementSibling;
        if (menu?.classList.contains('dropdown-menu')) {
            menu.classList.toggle('hidden');
        }
    });
}

/** GLightbox lightbox initialization — one instance per data-gallery group */
function initLightbox() {
    // Collect .glightbox elements, excluding Swiper loop clones (swiper-slide-duplicate).
    // Swiper's loop: true creates duplicate slides with full HTML copies; including them
    // would show duplicate images in the lightbox gallery.
    const glightboxElements = Array.from(
        document.querySelectorAll<HTMLElement>('.glightbox')
    ).filter((el) => !el.closest('.swiper-slide-duplicate'));

    // Group elements by their data-gallery attribute so each section gets its
    // own lightbox gallery.  Elements without data-gallery are placed in a
    // fallback group keyed by __ungrouped__.
    const UNGROUPED = '__ungrouped__';
    const groups = new Map<string, HTMLElement[]>();

    for (const el of glightboxElements) {
        const key = el.getAttribute('data-gallery') || UNGROUPED;
        let list = groups.get(key);
        if (!list) {
            list = [];
            groups.set(key, list);
        }
        list.push(el);
    }

    // Create one GLightbox instance per gallery group.
    // Note: GLightbox's bundled type declarations type `selector` as
    // `string | undefined` and `elements` as `[] | null`, which is too
    // narrow — the library actually accepts `null` for selector and any
    // array-like for elements.  We cast to satisfy the compiler.
    const lightboxes = new Map<string, ReturnType<typeof GLightbox>>();
    for (const [key, elements] of groups) {
        lightboxes.set(
            key,
            GLightbox({
                selector: null as unknown as undefined,
                elements: elements as unknown as [],
                touchNavigation: true,
                loop: true,
            })
        );
    }

    // Manual click binding: elements option disables selector, so no auto click handlers.
    // Use delegation so clicks on Swiper duplicates map to the correct gallery element.
    document.addEventListener('click', (e) => {
        const target = (e.target as HTMLElement).closest('.glightbox');
        if (!target) return;

        e.preventDefault();
        let elementToOpen: HTMLElement = target as HTMLElement;

        if (target.closest('.swiper-slide-duplicate')) {
            // Map duplicate click to original. Swiper adds duplicates at start+end; use module to map.
            const duplicateSlide = target.closest('.swiper-slide-duplicate');

            if (duplicateSlide) {
                // Swiper sets data-swiper-slide-index on every slide (including
                // duplicates) to reference the original slide's position.  Use it
                // to look up the matching non-duplicate slide and its .glightbox
                // element so the lightbox opens the correct image.
                const slideIndex = duplicateSlide.getAttribute('data-swiper-slide-index');
                const wrapper = duplicateSlide.parentElement;

                if (slideIndex !== null && wrapper) {
                    const originalSlide = wrapper.querySelector(
                        `.swiper-slide:not(.swiper-slide-duplicate)[data-swiper-slide-index="${slideIndex}"]`
                    );
                    const originalGlightbox = originalSlide?.querySelector(
                        '.glightbox'
                    ) as HTMLElement | null;

                    if (originalGlightbox && glightboxElements.includes(originalGlightbox)) {
                        elementToOpen = originalGlightbox;
                    }
                }
            }
        }

        if (!glightboxElements.includes(elementToOpen)) {
            return;
        }
        const key = elementToOpen.getAttribute('data-gallery') || UNGROUPED;
        const lightbox = lightboxes.get(key);
        const groupMembers = groups.get(key) ?? [];
        const indexInGroup = groupMembers.indexOf(elementToOpen);
        if (indexInGroup < 0 || !lightbox) {
            return;
        }
        // GLightbox sets `slideInfo.gallery` only when built from a selector, not
        // from the `elements` option. `open(clickedNode)` then runs
        // getGalleryElements() which compares `slideInfo.gallery === data-gallery`:
        // every item is filtered out (`undefined === 'foo'`), slides never build,
        // or slide.instance stays missing → crashes (e.g. reading 'create').
        // openAt(i) runs open(null, i) and skips that filter — correct for our setup.
        (lightbox as { openAt(index: number): void }).openAt(indexInGroup);
    });
}
